// SubtenantExtendedForm2.tsx
// редактируем квоты / наличие авто в таблице привязки сформированной в SubtenantForm
import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Button, 
  CircularProgress, 
  DialogActions, 
  DialogContent, 
  DialogTitle, 
  Typography
} from '@mui/material';
import { 
  GridColDef, 
  GridRowModel, 
  GridRowsProp,
  GridRowModesModel,
  GridEventListener,
  GridRowEditStopReasons,
  GridEditInputCell,
  GridRenderCellParams
} from '@mui/x-data-grid';
import CloseIcon from '@mui/icons-material/Close'; // Заменили иконку на Закрыть
import { EditableDataGrid } from './EditableDataGrid';
import { useFetch } from '../../../hooks/useFetch3';
import { api } from '../../../api/entityApi';
import { Subtenant, subtenantApi, SubtenantQuota } from '../../../api/subtenantApi';
import { Entity } from '../../../types/entity';

interface QuotaRow {
  id: string; 
  entity_id: number;
  quota_slots: number;
  quota_permits: number; 
  occupied_slots: number; 
}

interface SubtenantExtendedFormProps {
  subtenant: Subtenant | undefined | null;
  onClose: () => void;
}

export const SubtenantExtendedForm: React.FC<SubtenantExtendedFormProps> = ({
  subtenant,
  onClose,
}) => {
    // const [rows, setRows] = useState<GridRowsProp<QuotaRow>>([]);
    // const [loading, setLoading] = useState<boolean>(true);
    const [rowModesModel, setRowModesModel] = useState<GridRowModesModel>({});
    const { data: entity, error: entityError, loading: entityLoading} = useFetch<Entity[]>(api.get, [], { type: 1, });
    const { data: quota, error: quotaError, loading: quotaLoading, refresh } = useFetch<SubtenantQuota[]>(subtenantApi.getSubtenantQuota, 
        [], subtenant?.subtenant_id );

/*  useEffect(() => {
    if (!subtenant) return;
    
    setLoading(true);
    setTimeout(() => {
      const mockData: QuotaRow[] = [
        { id: '1', entity_id: 299, quota_slots: 10, quota_permits: 5, occupied_slots: 3 },
        { id: '2', entity_id: 299, quota_slots: 20, quota_permits: 12, occupied_slots: 8 },
        { id: '3', entity_id: 299, quota_slots: 15, quota_permits: 7, occupied_slots: 5 },
      ];
      setRows(mockData);
      setLoading(false);
    }, 600);
  }, [subtenant]);
*/

  const handleRowEditStop: GridEventListener<'rowEditStop'> = (params, event) => {
    if (params.reason === GridRowEditStopReasons.rowFocusOut) {
      event.defaultMuiPrevented = true;
    }
  };

/*
  // Важно: Эта функция в DataGrid может быть асинхронной (async)!
  // Она срабатывает автоматически, когда строка успешно отредактирована и сохранена локально.
  const processRowUpdate = async (newRow: GridRowModel<QuotaRow>, oldRow: GridRowModel<QuotaRow>) => {
    // Проверяем, изменилось ли хоть что-то, чтобы не слать пустые запросы
    if (JSON.stringify(newRow) === JSON.stringify(oldRow)) {
      return newRow;
    }

    try {
      // Отправляем ОДНУ измененную запись на бэкенд
      console.log('Отправляем на бэкенд строку:', newRow);
      // await api.updateSingleQuotaRow(subtenant?.id, newRow);
      
      // Обновляем локальный стейт, чтобы DataGrid отобразил новые данные
      const updatedRow = { ...newRow, isNew: false };
      setRows(rows.map((row) => (row.id === newRow.id ? updatedRow : row)));
      
      return updatedRow;
    } catch (error) {
      console.error('Не удалось сохранить строку:', error);
      // Если бэкенд вернул ошибку, возвращаем oldRow, чтобы DataGrid откатил изменения назад
      return oldRow; 
    }
  };

  const handleProcessRowUpdateError = (error: Error) => {
    console.error('Ошибка в процессе обновления:', error);
  };
*/
  const processRowUpdate = async (newRow: GridRowModel<SubtenantQuota>, oldRow: GridRowModel<SubtenantQuota>) => {
    if (JSON.stringify(newRow) === JSON.stringify(oldRow)) {
      return newRow;
    }

    try {
      // Отправляем ОДНУ измененную запись на бэкенд
      if (!await subtenantApi.updateSingleQuotaRow(newRow)) 
        return oldRow;
      
      return { ...newRow, isNew: false };
    } catch (error) {
      console.error('Не удалось сохранить строку:', error);
      // Если бэкенд вернул ошибку, возвращаем oldRow, чтобы DataGrid откатил изменения назад
      return oldRow; 
    }
  };

  const handleProcessRowUpdateError = (error: Error) => {
    console.error('Ошибка в процессе обновления:', error);
  };

  const baseColumns: GridColDef[] = [
    { field: 'entityId', headerName: 'Entity ID', flex: 1, editable: false,
        renderCell: (params: GridRenderCellParams<Entity>) => {
            return (
                <Typography>
                    {entity.find(e => e.entity_id === params.row.entity_id)?.name}
                </Typography>
            );
        }
     },
    { field: 'max_parking_slots', headerName: 'Quota Slots', type: 'number', flex: 1, editable: true },
    { field: 'max_permits', headerName: 'Quota Permits', type: 'number', flex: 1, editable: true },
    { field: 'occupied_parking_slots', headerName: 'Occupied Slots', type: 'number', flex: 1, editable: true },
  ];

  const columns = React.useMemo(() => {
    return baseColumns.map((col) => {
      if (col.type === 'number' && col.editable) {
        return {
          ...col,
          valueParser: (value: any) => Number(value),
          renderEditCell: (params: any) => (
            <GridEditInputCell
              {...params}
              inputProps={{
                onFocus: (event: React.FocusEvent<HTMLInputElement>) => {
                  event.target.select();
                },
              }}
            />
          ),
        };
      }
      return col;
    });
  }, [baseColumns]);

  if (quotaLoading || entityLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', bgcolor: 'background.paper' }}>
      <DialogTitle sx={{ pb: 0 }}>Редактирование квот</DialogTitle>
      <DialogTitle sx={{ pb: 2 }}>{subtenant?.name}</DialogTitle>
      
      <DialogContent dividers sx={{ p: 0, height: 350 }}>
        <EditableDataGrid
          rows={quota}
          // rows={rows}
          columns={columns}
          editMode="row"
          getRowId={(row)=>row.entity_id}
          rowModesModel={rowModesModel}
          onRowModesModelChange={(newModel) => setRowModesModel(newModel)}
          onRowEditStop={handleRowEditStop}
          processRowUpdate={processRowUpdate} // Магия авто-сохранения происходит здесь
          onProcessRowUpdateError={handleProcessRowUpdateError}
          disableRowSelectionOnClick
          hideFooter
        />
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button 
          onClick={onClose} 
          startIcon={<CloseIcon />} 
          color="primary"
          variant="outlined"
        >
          Закрыть
        </Button>
      </DialogActions>
    </Box>
  );
};